import { ChangeDetectionStrategy, Component, inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router, Data } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { noop, tap, Observable } from 'rxjs';
import { Store } from '@ngxs/store';
import { UserState } from '@state/user/state';
import { AccountState } from '@state/account/state';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { CompactType, DisplayGrid, GridsterConfig, GridType } from 'angular-gridster2';
import {
  CustomObjectValue,
  CustomObjectValueTemplateTab,
  CustomObjectValueTemplateTabLayout,
} from '../../custom-objects.types';
import { FieldDef } from '@core/models';
import { FeatureFlag } from '@core/enums';
import { guid } from '@shared/Utils/common.utils';
import { CustomObjectState } from '../../state/state';
import { CustomObjectActions } from '../../state/actions';
import { PermissionUtils } from '@core/utils/permission.utils';
import { EditToggle, PanelComponentBase, PanelDef } from '@shared/components/panels/panels.types';
import { SaveTemplateComponent } from '@shared/components/widgets/save-template/save-template.component';
import { SaveTemplateModalResult } from '@shared/components/widgets/save-template/save-template.types';

@Component({
  selector: 'app-custom-objects-dashboard-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomObjectsDashboardDetailsComponent extends OnDestroyMixin implements OnInit {
  gridOptions: GridsterConfig = {
    gridType: GridType.VerticalFixed,
    compactType: CompactType.None,
    displayGrid: DisplayGrid.OnDragAndResize,
    pushItems: true,
    margin: 0,
    minCols: 3,
    maxCols: 100,
    minRows: 1,
    maxRows: 100,
    maxItemCols: 100,
    minItemCols: 1,
    maxItemRows: 100,
    minItemRows: 1,
    maxItemArea: 2500,
    minItemArea: 1,
    defaultItemCols: 1,
    defaultItemRows: 1,
    fixedColWidth: 105,
    fixedRowHeight: 105,
    outerMargin: false,
    draggable: {
      enabled: false,
    },
    resizable: {
      enabled: false,
    },
    itemChangeCallback: this.panelChange.bind(this),
  };

  ui = {
    editMode: false,
    isLoading: true,
    name: '',
    panelIdInEditMode: null,
  };

  changeTokenPair: { state: string; newState: string };
  selectedValue$: Observable<CustomObjectValue>;
  selectedValue: CustomObjectValue;
  selectedValueMemento: CustomObjectValue;
  tabs: CustomObjectValueTemplateTab[];
  layoutItems: { [key: string]: CustomObjectValueTemplateTabLayout[] } = {};
  tabId?: string;
  relatedFieldDefs: FieldDef[] = [];
  public routeData: Data;

  stateSelectedValue$: Observable<CustomObjectValue> = inject(Store).select(CustomObjectState.getValueById);

  @ViewChildren('panels') panels: QueryList<PanelComponentBase<any>>;

  private get customObjectId(): number {
    return +this.activatedRoute.snapshot.params.customObjectId;
  }

  private get customObjectValueId(): number {
    return +this.activatedRoute.snapshot.params.customObjectValueId;
  }

  private get hasLayoutChange(): boolean {
    return this.changeTokenPair.state !== this.changeTokenPair.newState;
  }

  constructor(
    private readonly store: Store,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly matDialog: MatDialog,
  ) {
    super();
  }

  ngOnInit(): void {
    this.activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data: Data) => {
      this.routeData = data;
    });
    // value is set in the parent dashboard component.
    this.selectedValue$ = this.stateSelectedValue$.pipe(
      untilComponentDestroyed(this),
      tap((selectedValue) => {
        if (selectedValue && selectedValue.id === this.customObjectValueId) {
          this.selectedValueMemento = { ...selectedValue };
          this.selectedValue = selectedValue;
          this.tabs = selectedValue.template.definition.tabs.map(({ id, name }) => ({ id, name }));
          this.tabId = this.activatedRoute.snapshot.params.tabId
            ? this.activatedRoute.snapshot.params.tabId
            : this.tabs[0].id;
          this.ui = { ...this.ui, name: `${selectedValue.customObject.label} #${selectedValue.id}` };
          this.relatedFieldDefs = this.activatedRoute.snapshot.data.fieldDefs.filter(
            ({ related_to_type }) => related_to_type === selectedValue.customObject.name,
          );

          this.getMultiSelectFieldValue(this.selectedValue);

          this.layoutItems = this.selectedValue.template.definition.tabs.reduce(
            (p, c) => ({ ...p, [c.id]: c.layout }),
            {},
          );

          this.ui = { ...this.ui, isLoading: false };
        }
      }),
    );
  }

  onPanelAdded(panelDef: PanelDef<any>): void {
    this.layoutItems[this.tabId] = [
      ...this.layoutItems[this.tabId],
      { x: 0, y: 0, cols: 1, rows: 2, panel: { ...panelDef, id: guid() } },
    ];

    this.changeTokenPair = { ...this.changeTokenPair, newState: guid() };
  }

  panelChange() {
    this.changeTokenPair = { ...this.changeTokenPair, newState: guid() };
  }

  onPanelDefUpdated(panelDef: PanelDef<any>): void {
    const tab = this.selectedValue.template.definition.tabs.find(({ id }) => this.tabId === id);
    const layoutItem = tab.layout.find(({ panel: { id } }) => panelDef.id === id);
    layoutItem.panel = { ...panelDef };
    this.store.dispatch(new CustomObjectActions.UpdateCustomObjectValueTemplateDefinition(this.selectedValue.template));
  }

  onRemovePanel(item: CustomObjectValueTemplateTabLayout): void {
    this.layoutItems[this.tabId] = [
      ...this.layoutItems[this.tabId].filter(({ panel: { id } }) => item.panel.id !== id),
    ];
    const tab = this.selectedValue.template.definition.tabs.find(({ id }) => id === this.tabId);
    tab.layout = this.layoutItems[this.tabId];

    this.changeTokenPair = { ...this.changeTokenPair, newState: guid() };
  }

  onTabAdded(tab: CustomObjectValueTemplateTab): void {
    const tabModel: CustomObjectValueTemplateTab = { ...tab, id: guid() };
    this.tabs = [...this.tabs, { ...tabModel }];
    this.selectedValue.template.definition.tabs = [
      ...this.selectedValue.template.definition.tabs,
      { ...tabModel, layout: [] },
    ];

    this.changeTokenPair = { ...this.changeTokenPair, newState: guid() };
  }

  onTabUpdated(tab: CustomObjectValueTemplateTab): void {
    let tabModel = this.tabs.find(({ id }) => tab.id === id);
    tabModel.name = tab.name;
    tabModel = this.selectedValue.template.definition.tabs.find(({ id }) => tab.id === id);
    tabModel.name = tab.name;

    this.changeTokenPair = { ...this.changeTokenPair, newState: guid() };
  }

  onTabRemoved(tab: CustomObjectValueTemplateTab): void {
    delete this.layoutItems[tab.id];
    this.selectedValue.template.definition.tabs = this.selectedValue.template.definition.tabs.filter(
      ({ id }) => id !== tab.id,
    );
    this.tabs = [...this.tabs.filter(({ id }) => id !== tab.id)];

    this.changeTokenPair = { ...this.changeTokenPair, newState: guid() };
  }

  onTabMoved(event: CdkDragDrop<any[]>): void {
    moveItemInArray(this.tabs, event.previousIndex, event.currentIndex);
    moveItemInArray(this.selectedValue.template.definition.tabs, event.previousIndex, event.currentIndex);

    this.changeTokenPair = { ...this.changeTokenPair, newState: guid() };
  }

  onTabChange(tabId: string): void {
    const navigation = this.activatedRoute.snapshot.params.tabId
      ? this.router.navigate(['..', tabId], { relativeTo: this.activatedRoute })
      : this.router.navigate([tabId], { relativeTo: this.activatedRoute });
    navigation.then(() => (this.tabId = tabId));
  }

  onToggleEditMode(): void {
    this.ui = { ...this.ui, editMode: !this.ui.editMode };
    this.gridOptions = {
      ...this.gridOptions,
      resizable: { ...this.gridOptions.resizable, enabled: this.ui.editMode },
      draggable: { ...this.gridOptions.draggable, enabled: this.ui.editMode },
      displayGrid: this.ui.editMode ? DisplayGrid.Always : DisplayGrid.OnDragAndResize,
    };

    if (this.ui.editMode) {
      const state = guid();
      this.changeTokenPair = { state, newState: state };
    } else {
      this.changeTokenPair = null;
    }

    if (this.gridOptions.api?.optionsChanged) {
      this.gridOptions.api.optionsChanged();
    }
  }

  onLayoutSaved(cancel?: boolean): void {
    if (!cancel) {
      this.matDialog
        .open(SaveTemplateComponent, {
          autoFocus: false,
          data: {
            templateName: this.selectedValue.template.name,
            typeLabel: this.selectedValue.customObject.label_plural,
          },
        })
        .afterClosed()
        .pipe(
          untilComponentDestroyed(this),
          tap((result: SaveTemplateModalResult) => {
            if (result) {
              const tab = this.selectedValue.template.definition.tabs.find(({ id }) => this.tabId === id);
              tab.layout = this.layoutItems[this.tabId];
              this.ui = { ...this.ui, isLoading: true };

              if (result.saveMode === 'update') {
                this.store.dispatch(
                  new CustomObjectActions.UpdateCustomObjectValueTemplateDefinition(this.selectedValue.template),
                );
              } else if (result?.saveMode === 'create') {
                this.store.dispatch(
                  new CustomObjectActions.AddAndUpdateCustomObjectValueTemplate({
                    ...this.selectedValue,
                    template: {
                      ...this.selectedValue.template,
                      name: result.templateName,
                    },
                  }),
                );
              }
            } else {
              // If user cancel from save layout dialog, update definition to its initial state before the layout changes.
              this.store.dispatch(
                new CustomObjectActions.GetValueById({
                  id: this.customObjectValueId,
                  customObjectId: this.customObjectId,
                }),
              );
            }
          }),
        )
        .subscribe(noop);
    } else if (this.hasLayoutChange) {
      this.store.dispatch(
        new CustomObjectActions.GetValueById({
          id: this.customObjectValueId,
          customObjectId: this.customObjectId,
        }),
      );
    }

    this.onToggleEditMode();
  }

  onDataUpdated(updatedData: any): void {
    this.notifyPanels.bind(this);

    /**
     * [SC-48695] WIP:
     * BE service is not ready to handle value updates at customObjectValue top-level attributes.
     * That's why we need to pass the customObjectValue as is inside the data attribute,
     * and the BE will handle the modifications at the customObjectValue level.
     */

    // TODO: Remove this once BE is ready to handle value updates at customObjectValue top-level attributes.
    const { customObject, id } = this.selectedValue;

    this.store
      .dispatch(
        new CustomObjectActions.UpdateSelectedValue({
          // TODO: Uncomment this once BE is ready to handle value updates at customObjectValue top-level attributes.
          // ...this.selectedValue,
          // ...updatedData,
          // TODO: Remove below once BE is ready to handle value updates at customObjectValue top-level attributes.
          id,
          data: { ...this.selectedValue, ...updatedData },
          customObject,
        }),
      )
      .subscribe(() => this.notifyPanels());
    this.ui = { ...this.ui, isLoading: true };
  }

  onEditToggled(event: EditToggle<any>): void {
    if (event.toggle) {
      this.ui = { ...this.ui, panelIdInEditMode: event.panelDef.id };
    } else {
      this.ui = { ...this.ui, panelIdInEditMode: null };
    }
  }

  private notifyPanels(): void {
    this.panels.forEach((panel) => {
      const notifiablePanels = ['form', 'matrix'];

      if (notifiablePanels.includes(panel.panelDef.type)) {
        panel.component.instance.completeSaving(false);
      }
    });
  }

  private getMultiSelectFieldValue(customObjectValue: any): void {
    this.activatedRoute.snapshot.data.fieldDefs
      .filter(({ name, meta }) => Object.keys(this.selectedValue).includes(name) && meta?.multi_select)
      .forEach(({ name }) => (this.selectedValue[name] = customObjectValue[name]));
  }

  canEditLayout(customObjectName: string): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      const permission = PermissionUtils.generateCustomObjectPermissions(customObjectName);
      return this.store.selectSnapshot(UserState.hasPermission(permission.MANAGE_LAYOUT));
    }
    return true;
  }
}
